/**
 * The site's light or dark look, chosen per browser: System (the default)
 * follows the device's dark mode as it changes; Light and Dark hold. The
 * palettes are the CSS variables in index.css; this decides which one <html>
 * wears, by the `dark` class.
 *
 * readThemeChoice, resolveTheme, applyTheme and bootTheme are self-contained:
 * themeBootScript inlines their source into index.html, where they run before
 * first paint. They may use only their arguments, each other and the DOM.
 */

export type ThemeChoice = 'system' | 'light' | 'dark';
export type Theme = 'light' | 'dark';
/** Why the saved choice was not used: the browser refused to read storage, or the saved value is not a choice. */
export type ThemeProblem = 'unreadable' | 'unknown' | null;

export interface ThemeReading {
  choice: ThemeChoice;
  problem: ThemeProblem;
  /** What the page wears now: the choice, with System resolved against the device. */
  theme: Theme;
}

/** Per browser, not per account: a device preference, so no database column. */
export const THEME_STORAGE_KEY = 'cerebro-diem:theme';
export const DARK_QUERY = '(prefers-color-scheme: dark)';

/** This browser's saved choice. Nothing saved, an unreadable storage and an unknown value all mean System; the last two say so. */
export function readThemeChoice(storageKey: string): { choice: ThemeChoice; problem: ThemeProblem } {
  let stored: string | null;
  try {
    stored = window.localStorage.getItem(storageKey);
  } catch {
    return { choice: 'system', problem: 'unreadable' };
  }
  if (stored === null) return { choice: 'system', problem: null };
  if (stored === 'system' || stored === 'light' || stored === 'dark') return { choice: stored, problem: null };
  return { choice: 'system', problem: 'unknown' };
}

export function resolveTheme(choice: ThemeChoice, systemDark: boolean): Theme {
  if (choice === 'system') return systemDark ? 'dark' : 'light';
  return choice;
}

export function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle('dark', theme === 'dark');
}

/** Reads the saved choice, resolves it against the device's dark mode now, and puts it on <html>. */
export function bootTheme(storageKey: string, darkQuery: string): ThemeReading {
  const { choice, problem } = readThemeChoice(storageKey);
  const theme = resolveTheme(choice, window.matchMedia(darkQuery).matches);
  applyTheme(theme);
  return { choice, problem, theme };
}

/** The pre-paint script index.html runs: these same functions, so the page and the app never disagree. */
export function themeBootScript(): string {
  const source = [readThemeChoice, resolveTheme, applyTheme, bootTheme].map(String).join('\n');
  return `(function () {\n${source}\nbootTheme(${JSON.stringify(THEME_STORAGE_KEY)}, ${JSON.stringify(DARK_QUERY)});\n})();`;
}
